import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';
import { Table, Label } from 'semantic-ui-react';

import { useAppDispatch } from '../../../hooks';
import Grid from '../../../components/common/grid';
import ContextMenu from '../../../components/common/grid/contextMenu';
import { gridKeys } from '../../../constants/gridKeys';
import { RootState } from '../../../store';
import RecordResultMessage from '../../../components/common/recordResult';
import { Common } from '../../../components';
import Panel from '../../../components/common/grid/managed/panel';
import { systemSettingsRoutes } from '../../systemSettings/constants';
import { WriteOnly } from '../../../components/common/grid/WriteOnly';
import { Record as RecordUtil } from '../../../modules/bin/utility';

import {
  clearEventOrganizerErrors,
  getEventOrganizerListData,
  getEventOrganizerListDataBySearchTerm,
  getEventOrganizerMetadata,
  deleteEventOrganizer,
} from '../../eventOrganizers/store/eventOrganizerActions';
import { eventOrganizerColumns } from '../../../constants/eventOrganizer';
import { IEventOrganizer } from '../../../models/EventOrganizer';
import DeleteEventOrganizerModal from './DeleteEventOrganizerModal';
import { CONTEXT_MENU_KEYS } from '../../../constants/contextMenuEntries';

const getPanel = (onCreate: () => void) => (
  <Panel name='right'>
    <WriteOnly>
      <Common.Button primary onClick={onCreate}>
        <Translate value='eventOrganizers.page.view.create-action' />
      </Common.Button>
    </WriteOnly>
  </Panel>
);

const EventOrganizersContent = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const state = useSelector((s: RootState) => (s as any).eventOrganizer);

  const [deleteTarget, setDeleteTarget] = useState<IEventOrganizer | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          getMetadata: getEventOrganizerMetadata,
          getListData: getEventOrganizerListData,
          clearRecordMessages: clearEventOrganizerErrors,
          getListDataBySearchTerm: getEventOrganizerListDataBySearchTerm,
        },
        dispatch
      ),
    [dispatch]
  );

  const openDelete = useCallback((item: IEventOrganizer) => {
    // Clear any lingering success/error from a previous delete so this
    // session's close-on-success effect doesn't fire immediately, and a
    // previous error message doesn't pre-fill the modal for a new target.
    dispatch(clearEventOrganizerErrors('recordDelete') as any);
    setDeleteError(null);
    setDeleteTarget(item);
  }, [dispatch]);

  const closeDelete = useCallback(() => {
    setDeleteTarget(null);
    setDeleteError(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget?.id) return;
    dispatch(deleteEventOrganizer(deleteTarget.id) as any);
  }, [deleteTarget, dispatch]);

  React.useEffect(() => {
    const r = state?.recordDelete;
    if (deleteTarget && r?.ready === 0 && r?.success?.show) {
      closeDelete();
    } else if (deleteTarget && r?.error?.show) {
      const key = r?.error?.errorKeys?.[0];
      setDeleteError(
        key ? I18n.t(`error-messages.${key}`, { defaultValue: key }) : I18n.t('eventOrganizers.page.delete.genericError')
      );
    }
  }, [state?.recordDelete, deleteTarget, closeDelete]);

  const contextMenu = (
    <ContextMenu>
      <ContextMenu.Item
        key='event-organizer-edit'
        label={I18n.t('eventOrganizers.page.view.edit-action')}
        writeAction
        onClick={(_id: number, item: IEventOrganizer) => {
          if (item?.id) {
            navigate(`${systemSettingsRoutes.EVENT_ORGANIZERS_EDIT}/${item.id}`);
          }
        }}
      />
      <ContextMenu.Item
        key='event-organizer-delete'
        label={I18n.t('eventOrganizers.page.view.delete-action')}
        writeAction
        isHiden={(item: IEventOrganizer) => item?.enabled === false}
        onClick={(_id: number, item: IEventOrganizer) => {
          if (item?.id) openDelete(item);
        }}
      />
      <ContextMenu.Item
        key={CONTEXT_MENU_KEYS.EVENT_ORGANIZERS.EVENTS.key}
        label={I18n.t('eventOrganizers.page.view.events-action')}
        onClick={(_id: number, item: IEventOrganizer) => {
          if (item?.id) {
            navigate(CONTEXT_MENU_KEYS.EVENT_ORGANIZERS.EVENTS.getUrl({ id: item.id, name: item.name }, {}));
          }
        }}
      />
    </ContextMenu>
  );

  // Render the name cell with strikethrough + Deleted badge when the API marks
  // the row as disabled (soft-delete).
  const columnRender = useCallback((col: any, colData: any, item: IEventOrganizer) => {
    if (col.id !== eventOrganizerColumns.NAME) return null;
    if (item?.enabled === false) {
      return (
        <Table.Cell key={`${col.id}-deleted`}>
          <span style={{ textDecoration: 'line-through', color: 'var(--text-muted, #888)' }}>
            {colData}
          </span>
          <Label size='tiny' color='red' style={{ marginLeft: 8 }}>
            <Translate value='eventOrganizers.page.view.deletedBadge' />
          </Label>
        </Table.Cell>
      );
    }
    return null;
  }, []);

  const panel = getPanel(() => navigate(systemSettingsRoutes.EVENT_ORGANIZERS_CREATE));
  const clearListMessages = () => dispatch(clearEventOrganizerErrors(gridKeys.EVENT_ORGANIZERS) as any);

  return (
    <>
      <RecordResultMessage
        record={state.record}
        onDismiss={() => dispatch(clearEventOrganizerErrors('record') as any)}
      />
      <h2>
        <Translate value='eventOrganizers.page.view.title' />
      </h2>
      <RecordResultMessage
        record={state.eventOrganizersList}
        onDismiss={clearListMessages}
      />
      <Grid.Managed
        listKey={gridKeys.EVENT_ORGANIZERS}
        list={state.eventOrganizersList}
        actions={{ ...actions }}
        search
        filtersButton={false}
        columnRender={columnRender}
      >
        {panel}
        {contextMenu}
      </Grid.Managed>
      <DeleteEventOrganizerModal
        isOpen={!!deleteTarget}
        item={deleteTarget}
        isDeleting={RecordUtil.isRecordLoading(state?.recordDelete)}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={closeDelete}
      />
    </>
  );
};

export default EventOrganizersContent;
